import * as React from "react";
import { Check, ChevronDown, X, Users, Shield, UserCheck, UsersIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type RoleOption = {
  label: string;
  value: string;
  description?: string;
};

export type UserOption = {
  label: string;
  value: string;
  email?: string;
  role?: string;
};

export type TeamOption = {
  label: string;
  value: string;
  type?: string;
  manager?: string;
};

export interface PipelineAccessSelectProps {
  roleOptions: RoleOption[];
  userOptions: UserOption[];
  teamOptions: TeamOption[];
  selectedRoles: string[];
  selectedUsers: string[];
  selectedTeams: string[];
  onRolesChange: (selected: string[]) => void;
  onUsersChange: (selected: string[]) => void;
  onTeamsChange: (selected: string[]) => void;
  className?: string;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  maxDisplay?: number;
}

export function PipelineAccessSelect({
  roleOptions = [],
  userOptions = [],
  teamOptions = [],
  selectedRoles = [],
  selectedUsers = [],
  selectedTeams = [],
  onRolesChange,
  onUsersChange,
  onTeamsChange,
  className,
  placeholder = "Configure access...",
  id,
  disabled = false,
  maxDisplay = 3,
}: PipelineAccessSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleTeamSelect = (value: string) => {
    const newSelected = selectedTeams.includes(value)
      ? selectedTeams.filter((item) => item !== value)
      : [...selectedTeams, value];
    onTeamsChange(newSelected);
  };

  const handleRoleSelect = (value: string) => {
    const newSelected = selectedRoles.includes(value)
      ? selectedRoles.filter((item) => item !== value)
      : [...selectedRoles, value];
    onRolesChange(newSelected);
  };

  const handleUserSelect = (value: string) => {
    const newSelected = selectedUsers.includes(value)
      ? selectedUsers.filter((item) => item !== value)
      : [...selectedUsers, value];
    onUsersChange(newSelected);
  };

  const handleSelectAllTeams = () => {
    onTeamsChange(teamOptions.map(option => option.value));
  };

  const handleClearAllTeams = () => {
    onTeamsChange([]);
  };

  const handleSelectAllRoles = () => {
    onRolesChange(roleOptions.map(option => option.value));
  };

  const handleClearAllRoles = () => {
    onRolesChange([]);
  };

  const handleSelectAllUsers = () => {
    onUsersChange(userOptions.map(option => option.value));
  };

  const handleClearAllUsers = () => {
    onUsersChange([]);
  };

  const handleRemoveTeam = (value: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onTeamsChange(selectedTeams.filter(item => item !== value));
  };

  const handleRemoveRole = (value: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onRolesChange(selectedRoles.filter(item => item !== value));
  };

  const handleRemoveUser = (value: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onUsersChange(selectedUsers.filter(item => item !== value));
  };

  const getSelectedTeamLabels = () => {
    return selectedTeams.map(value => 
      teamOptions.find(option => option.value === value)?.label || value
    );
  };

  const getSelectedRoleLabels = () => {
    return selectedRoles.map(value => 
      roleOptions.find(option => option.value === value)?.label || value
    );
  };

  const getSelectedUserLabels = () => {
    return selectedUsers.map(value => 
      userOptions.find(option => option.value === value)?.label || value
    );
  };

  const selectedTeamLabels = getSelectedTeamLabels();
  const selectedRoleLabels = getSelectedRoleLabels();
  const selectedUserLabels = getSelectedUserLabels();
  const allTeamsSelected = selectedTeams.length === teamOptions.length;
  const allRolesSelected = selectedRoles.length === roleOptions.length;
  const allUsersSelected = selectedUsers.length === userOptions.length;
  const noTeamsSelected = selectedTeams.length === 0;
  const noRolesSelected = selectedRoles.length === 0;
  const noUsersSelected = selectedUsers.length === 0;
  const nothingSelected = noTeamsSelected && noRolesSelected && noUsersSelected;
  const allSelected = allTeamsSelected && allRolesSelected && allUsersSelected;

  const totalSelected = selectedRoles.length + selectedTeams.length + selectedUsers.length;
  const allLabels = [...selectedRoleLabels, ...selectedTeamLabels, ...selectedUserLabels];

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            type="button"
            className={cn(
              "flex h-auto min-h-[40px] w-full items-start justify-between rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer hover:bg-primary/10 hover:border-primary",
              nothingSelected ? "text-muted-foreground" : "",
              className
            )}
          >
            <div className="flex flex-1 flex-wrap gap-1 items-start pointer-events-none">
              {nothingSelected ? (
                <span className="text-muted-foreground py-1">{placeholder}</span>
              ) : allSelected ? (
                <div className="flex items-center gap-1 py-1">
                  <Shield className="w-4 h-4 text-primary" />
                  <span className="text-primary font-medium">All access (Open pipeline)</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1 max-w-full">
                  {/* Show role badges first */}
                  {selectedRoleLabels.slice(0, maxDisplay).map((label, index) => {
                    const value = selectedRoles[index];
                    return (
                      <Badge
                        key={`role-${value}`}
                        variant="secondary"
                        className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 text-xs py-1 px-2 max-w-[120px] truncate"
                      >
                        <Shield className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                        <button
                          onClick={(e) => handleRemoveRole(value, e)}
                          className="ml-1 hover:bg-primary/30 rounded-full p-0.5 flex-shrink-0 pointer-events-auto"
                          disabled={disabled}
                          type="button"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                  
                  {/* Show team badges second */}
                  {selectedTeamLabels.slice(0, Math.max(0, maxDisplay - selectedRoleLabels.length)).map((label, index) => {
                    const value = selectedTeams[index];
                    return (
                      <Badge
                        key={`team-${value}`}
                        variant="secondary"
                        className="bg-success/5 text-success border-success/20 hover:bg-success/10 text-xs py-1 px-2 max-w-[120px] truncate"
                      >
                        <UsersIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                        <button
                          onClick={(e) => handleRemoveTeam(value, e)}
                          className="ml-1 hover:bg-success/20 rounded-full p-0.5 flex-shrink-0 pointer-events-auto"
                          disabled={disabled}
                          type="button"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                  
                  {/* Show user badges last */}
                  {selectedUserLabels.slice(0, Math.max(0, maxDisplay - selectedRoleLabels.length - selectedTeamLabels.length)).map((label, index) => {
                    const value = selectedUsers[index];
                    return (
                      <Badge
                        key={`user-${value}`}
                        variant="secondary"
                        className="bg-accent/5 text-accent border-accent/20 hover:bg-accent/10 text-xs py-1 px-2 max-w-[120px] truncate"
                      >
                        <UserCheck className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                        <button
                          onClick={(e) => handleRemoveUser(value, e)}
                          className="ml-1 hover:bg-accent/20 rounded-full p-0.5 flex-shrink-0 pointer-events-auto"
                          disabled={disabled}
                          type="button"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                  
                  {/* Show overflow indicator */}
                  {totalSelected > maxDisplay && (
                    <Badge variant="outline" className="text-xs">
                      +{totalSelected - maxDisplay} more
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <ChevronDown className="h-4 w-4 opacity-50 ml-2 flex-shrink-0 mt-1 pointer-events-none" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0 z-50" 
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <div>
            <div className="flex items-center border-b px-3 py-2 bg-muted/50">
              <Users className="w-4 h-4 mr-2 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">
                Pipeline Access ({totalSelected} selected)
              </span>
            </div>
            
            <Tabs defaultValue="roles" className="w-full">
              <TabsList className="grid grid-cols-3 w-full rounded-none border-b">
                <TabsTrigger value="roles" className="rounded-none">
                  <Shield className="w-4 h-4 mr-1" />
                  Roles ({roleOptions.length})
                </TabsTrigger>
                <TabsTrigger value="teams" className="rounded-none">
                  <UsersIcon className="w-4 h-4 mr-1" />
                  Teams ({teamOptions.length})
                </TabsTrigger>
                <TabsTrigger value="users" className="rounded-none">
                  <UserCheck className="w-4 h-4 mr-1" />
                  Users ({userOptions.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="roles" className="mt-0">
                {/* Role Quick Actions */}
                <div className="flex gap-1 p-2 border-b">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllRoles}
                    disabled={allRolesSelected || disabled}
                    className="flex-1 text-xs h-7"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAllRoles}
                    disabled={noRolesSelected || disabled}
                    className="flex-1 text-xs h-7"
                  >
                    Clear All
                  </Button>
                </div>

                <div 
                  className="max-h-64 overflow-auto"
                  onWheel={(e) => {
                    // Allow wheel scrolling within this container
                    e.stopPropagation();
                  }}
                >
                  {roleOptions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">No roles found.</div>
                  ) : (
                    <div className="p-1">
                      {roleOptions.map((option) => {
                        const isSelected = selectedRoles.includes(option.value);
                        return (
                          <div
                            key={option.value}
                            onClick={() => handleRoleSelect(option.value)}
                            className={cn(
                              "flex items-center space-x-2 cursor-pointer p-2 rounded-md text-sm transition-colors",
                              isSelected
                                ? "bg-primary/10 text-primary font-medium hover:bg-primary/20"
                                : "text-foreground hover:bg-primary/10 hover:text-primary"
                            )}
                          >
                            <div className="flex h-4 w-4 items-center justify-center rounded border border-primary">
                              {isSelected && <Check className="h-3 w-3 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">{option.label}</div>
                              {option.description && (
                                <div className="text-xs text-muted-foreground">
                                  {option.description}
                                </div>
                              )}
                            </div>
                            <Shield className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="teams" className="mt-0">
                {/* Team Quick Actions */}
                <div className="flex gap-1 p-2 border-b">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllTeams}
                    disabled={allTeamsSelected || disabled}
                    className="flex-1 text-xs h-7"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAllTeams}
                    disabled={noTeamsSelected || disabled}
                    className="flex-1 text-xs h-7"
                  >
                    Clear All
                  </Button>
                </div>

                <div 
                  className="max-h-64 overflow-auto"
                  onWheel={(e) => {
                    // Allow wheel scrolling within this container
                    e.stopPropagation();
                  }}
                >
                  {teamOptions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">No teams found.</div>
                  ) : (
                    <div className="p-1">
                      {teamOptions.map((option) => {
                        const isSelected = selectedTeams.includes(option.value);
                        return (
                          <div
                            key={option.value}
                            onClick={() => handleTeamSelect(option.value)}
                            className={cn(
                              "flex items-center space-x-2 cursor-pointer p-2 rounded-md text-sm transition-colors",
                              isSelected
                                ? "bg-success/5 text-success font-medium hover:bg-success/10"
                                : "text-foreground hover:bg-primary/10 hover:text-primary"
                            )}
                          >
                            <div className="flex h-4 w-4 items-center justify-center rounded border border-primary">
                              {isSelected && <Check className="h-3 w-3 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {option.type && `${option.type}`} {option.manager && `• Manager: ${option.manager}`}
                              </div>
                            </div>
                            <UsersIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="users" className="mt-0">
                {/* User Quick Actions */}
                <div className="flex gap-1 p-2 border-b">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllUsers}
                    disabled={allUsersSelected || disabled}
                    className="flex-1 text-xs h-7"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAllUsers}
                    disabled={noUsersSelected || disabled}
                    className="flex-1 text-xs h-7"
                  >
                    Clear All
                  </Button>
                </div>

                <div 
                  className="max-h-64 overflow-auto"
                  onWheel={(e) => {
                    // Allow wheel scrolling within this container
                    e.stopPropagation();
                  }}
                >
                  {userOptions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">No users found.</div>
                  ) : (
                    <div className="p-1">
                      {userOptions.map((option) => {
                        const isSelected = selectedUsers.includes(option.value);
                        return (
                          <div
                            key={option.value}
                            onClick={() => handleUserSelect(option.value)}
                            className={cn(
                              "flex items-center space-x-2 cursor-pointer p-2 rounded-md text-sm transition-colors",
                              isSelected
                                ? "bg-accent/5 text-accent font-medium hover:bg-accent/10"
                                : "text-foreground hover:bg-primary/10 hover:text-primary"
                            )}
                          >
                            <div className="flex h-4 w-4 items-center justify-center rounded border border-primary">
                              {isSelected && <Check className="h-3 w-3 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {option.email} {option.role && `• ${option.role}`}
                              </div>
                            </div>
                            <UserCheck className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Selected count info */}
      {!nothingSelected && (
        <div className="mt-1 text-xs text-muted-foreground">
          {allSelected
            ? "All roles, teams and users have access to this pipeline" 
            : `${selectedRoles.length} role${selectedRoles.length !== 1 ? 's' : ''}, ${selectedTeams.length} team${selectedTeams.length !== 1 ? 's' : ''} and ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''} selected`
          }
        </div>
      )}
    </div>
  );
} 